import { getMedias } from "@omni/mappers/product/shared/media.mapper";
import { CartItem as CartItemModel } from "@omni/types/api/cart/bff/shared/cartItem.model";
import { CartPromotion } from "@omni/types/api/cart/bff/shared/cartPromotion.model";
import { CallToActionType } from "@omni/types/api/cart/bff/shared/enums/callToActionType.enum";
import { Product } from "@omni/types/api/product/bff/product.model";
import { DeliveryPromiseType } from "@omni/types/api/product/bff/shared/enums/deliveryPromiseType.enum";
import { DeliveryTimeUnit } from "@omni/types/api/product/bff/shared/enums/deliveryTimeUnit.enum";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { PromoPackType } from "@omni/types/api/product/bff/shared/enums/promoPackType.enum";
import { Anchor } from "@omni/ui/components/Anchor/anchor.component";
import { Button } from "@omni/ui/components/Button/button.component";
import { head } from "@omni/utils/array.utils";
import * as CartUtils from "@omni/utils/cart.utils";
import { cn } from "@omni/utils/styles";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import CartItemInfo from "@/components/Cart/ShoppingCart/productCollectionBlock/cartItem/cartItemInfo/cartItemInfo.component";
import SwitchDelivery from "@/components/Cart/ShoppingCart/productCollectionBlock/cartItem/switchDelivery/switchDelivery.component";
import { ConfigurableProductImageComponent } from "@/components/Cart/ShoppingCart/productCollectionBlock/configurableProductImage/configurableProductImage.component";
import ErrorMessage from "@/components/ErrorMessages/errorMessages.component";
import { Row } from "@/components/General/Grid/row.component";
import { Modal } from "@/components/General/Modal/modal";
import { Tooltip } from "@/components/General/Tooltip/tooltip.component";
import { Amount } from "@/components/Product/Amount/amount.component";
import { ShoppingCartItemRenditions } from "@/components/Product/ProductImage/mappedRenditions";
import { ProductImageContainer } from "@/components/Product/ProductImage/productImage.container";
import WishlistButton from "@/components/Wishlist/WishlistButton/WishlistButton.component";
import { ProductCallToAction } from "@/redux/modules/api/data/cart/@types/cart.types";
import { APIStatus } from "@/redux/modules/api/data/cart/cart.module";
import { getAdditionalServicesForCartId } from "@/redux/modules/api/data/cart/cart.selectors";
import { WishlistModel } from "@/redux/modules/api/data/wishlist/@types/wishlist.model";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { Config } from "@/utils/config.utils";
import { formatPrice } from "@/utils/price.utils";
import {
    getProductConfiguration,
    productConfigurations
} from "@/utils/productConfiguration/productConfiguration.utils";

import { Accessories } from "./cartItemInfo/accessories/accessories.container";
import AdditionalServices, {
    CartAdditionalService
} from "./cartItemInfo/additionalServices/additionalServices.container";
import { CartItemPrice } from "./price/cartItemPrice.component";

type Props = {
    cartItem: CartItemModel;
    removeFromCart: (
        id: string,
        quantity: number,
        isFreeEntryAddedByCustomer: boolean
    ) => void;
    updateCartItemConfig: (
        id: string,
        config: Record<string, unknown>,
        productSubType: string
    ) => Promise<any>;
    moveToWishlist: (product: CartItemModel) => Promise<any>;
    removeFromWishlist: (sku: string) => Promise<any>;
    wishlists: WishlistModel[];
    onQuantityChange: (newQuantity: number, oldQuantity: number) => void;
    changeDeliveryType: (newDeliveryType: string) => void;
    productCtas?: ProductCallToAction;
    ccTooltipInfo: string;
    dsvTooltipInfo: string;
    warrantyDepositTooltipInfo: string;
    cashbackTooltipInfo: string;
    isUpdatingCart: boolean;
    hasProductMultipleCartEntries: boolean;
    onRemoveAdditionalService: (serviceCartItemId: string) => void;
    onAddAdditionalService: (cartItemId: string, serviceSku: string) => void;
    onAddAccessory: (
        cartItemId: string,
        sku: string,
        product: Product<ProductFieldSet.ProductTile>
    ) => Promise<void>;
    paintConfiguratorPath?: string;
};

const CartItem = ({
    cartItem,
    removeFromCart,
    onQuantityChange,
    changeDeliveryType,
    productCtas,
    ccTooltipInfo,
    dsvTooltipInfo,
    warrantyDepositTooltipInfo,
    cashbackTooltipInfo,
    isUpdatingCart,
    hasProductMultipleCartEntries,
    updateCartItemConfig,
    wishlists,
    moveToWishlist,
    removeFromWishlist,
    onAddAdditionalService,
    onAddAccessory,
    paintConfiguratorPath,
    onRemoveAdditionalService
}: Props) => {
    const { t } = useGranite();
    const [inAnimation, setInAnimation] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [error, setError] = useState(false);
    const [configurationValid, setConfigurationValid] = useState(true);
    const additionalServicesItems = useSelector(
        getAdditionalServicesForCartId(cartItem.id)
    );

    const productConfiguration = useMemo(
        () => getProductConfiguration({ sku: cartItem.sku }),
        [cartItem.sku]
    );

    const [configuration, setConfiguration] = useState<Record<string, unknown>>(
        CartUtils.getConfiguration(cartItem, productConfigurations) || {}
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isUpdatingCart && !inAnimation) {
            setInAnimation(isUpdatingCart);
            setTimeout(() => setInAnimation(false), 1400);
        }
    }, [isUpdatingCart]);

    const hideDeleteButton = (productObject: CartItemModel): boolean => {
        return productObject.free && !productObject.freeEntryAddedByCustomer;
    };

    const determinePromoLabels = (
        promotions: Array<CartPromotion>,
        onMobile: boolean
    ) => {
        return promotions
            .filter((promotion) => promotion.commercialText !== null)
            .map((promotion, index) => {
                return createPromoLabel(promotion, index, onMobile);
            });
    };

    const createPromoLabel = (
        promotion: CartPromotion,
        index: number,
        onMobile: boolean
    ) => {
        let tooltip = null;

        if (PromoPackType.Cashback === promotion.promotionType) {
            tooltip = cashbackTooltipInfo;
        }

        return (
            <div
                key={index}
                className={cn("tw:text-success tw:wrap-break-word", {
                    "tw:sm:hidden tw:pb-3": onMobile,
                    "tw:hidden tw:sm:block": !onMobile
                })}
            >
                {promotion.commercialText}
                {tooltip && <Tooltip innerHTML={tooltip} />}
            </div>
        );
    };

    const minimumOrderQuantity =
        cartItem.deliveryType === DeliveryType.HomeDelivery &&
        cartItem.product?.minimumOrderQuantity
            ? cartItem.product?.minimumOrderQuantity
            : 1;

    const hasCta = (): boolean => {
        // If positive delivery promise, we don't show CTA and confirm silently,
        // hence no need to show red accent on cartitem.
        if (
            productCtas?.actions.some(
                (cta) =>
                    cta.type ===
                    CallToActionType.ACCEPT_POSITIVE_DELIVERY_PROMISE
            )
        ) {
            return false;
        }

        return !!productCtas;
    };

    const hasSingleRemoveArticle =
        productCtas?.actions.length === 1 &&
        head(productCtas?.actions)?.type === CallToActionType.REMOVE_ARTICLE;

    const onMoveToWishlist = () => {
        moveToWishlist(cartItem);
    };

    const onRemoveFromWishlist = () => {
        removeFromWishlist(cartItem.sku);
    };

    const additionalServicesCartItems = (cartItem.additionalServices || []).map(
        (as) =>
            ({
                ...as,
                cartItemId: as.id,
                isSelected: additionalServicesItems.some(
                    (asci) => asci.sku === as.sku
                ),
                unitPrice: cartItem.product?.references?.montages?.find(
                    (montage) => montage.sku === as.sku
                )?.price,
                totalPrice: as.price,
                childAdditionalService: as.childAdditionalService,
                additionalServiceAllowed: as.additionalServiceAllowed
            }) satisfies CartAdditionalService
    ) as CartAdditionalService[];

    cartItem.product?.references?.montages?.forEach((m) => {
        if (!additionalServicesCartItems.some((i) => i.sku === m.sku)) {
            additionalServicesCartItems.push({
                sku: m.sku,
                title: m.title,
                unitPrice: m.price,
                isSelected: false,
                childAdditionalService: false,
                additionalServiceAllowed: cartItem.additionalServiceAllowed
            });
        }
    });

    const hasWarning = cartItem.messages?.at(0)?.type === "warning";
    const hasError = cartItem.messages?.at(0)?.type === "error" || hasCta();

    return (
        <div
            data-test={`cartItem-${cartItem.sku}`}
            key={cartItem.sku}
            className={cn("tw:relative", {
                "tw:after:content-[''] tw:after:animate-loading-bar tw:after:block tw:after:h-full tw:after:w-4 tw:after:absolute tw:after:top-0 tw:after:left-0 tw:after:p-0 tw:after:opacity-30 tw:after:bg-grey-light":
                    inAnimation
            })}
        >
            <Row
                className={cn("tw:bg-white tw:mt-2 tw:items-center", {
                    "tw:border-l-[3px] tw:border-l-warning":
                        hasWarning && !hasError,
                    "tw:border-l-[3px] tw:border-l-danger": hasError
                })}
            >
                <div className={cn("col-3 col-md-2 tw:lg:px-12")}>
                    {!cartItem.warrantyDeposit &&
                        !cartItem.articleConfiguration &&
                        cartItem.product?.url && (
                            <a
                                data-test={`cartItem-${cartItem.sku}-link-image`}
                                href={
                                    CartUtils.getUrl(
                                        cartItem,
                                        productConfigurations,
                                        Config.pages.giftcard
                                    )!
                                }
                                className="d-block"
                            >
                                <ProductImageContainer
                                    title={CartUtils.getTitle(cartItem, t)}
                                    medias={getMedias(
                                        cartItem.warrantyDeposit,
                                        CartUtils.getTitle(cartItem, t),
                                        cartItem.product.mainImages,
                                        cartItem.articleConfiguration
                                    )}
                                    renditions={ShoppingCartItemRenditions}
                                    isEcoChequesAccepted={false}
                                />
                            </a>
                        )}
                    {cartItem.articleConfiguration && (
                        <ConfigurableProductImageComponent
                            title={CartUtils.getTitle(cartItem, t)}
                            imageUrl={
                                cartItem.articleConfiguration.imageURL ??
                                undefined
                            }
                        />
                    )}
                </div>
                <CartItemInfo
                    cartItem={cartItem}
                    deliveryPromise={
                        hasSingleRemoveArticle
                            ? {
                                  deliveryPromiseType:
                                      DeliveryPromiseType.NoStock,
                                  deliveryTime: 0,
                                  deliveryTimeInHours: 0,
                                  deliveryTimeUnit: DeliveryTimeUnit.Hours
                              }
                            : cartItem.deliveryPromise
                    }
                    withRemove={hasSingleRemoveArticle}
                    ccTooltipInfo={ccTooltipInfo}
                    dsvTooltipInfo={dsvTooltipInfo}
                    productConfiguration={productConfiguration}
                    warrantyDepositTooltipInfo={warrantyDepositTooltipInfo}
                    promoLabels={determinePromoLabels(
                        cartItem.promotions,
                        false
                    )}
                />
                {determinePromoLabels(cartItem.promotions, true)}
                <div
                    className={cn(
                        "tw:hidden tw:lg:block col-lg-1 tw:text-right tw:lg:text-center"
                    )}
                >
                    {!cartItem.free && (
                        <>
                            {CartUtils.showStrippedPrice(cartItem) && (
                                <div
                                    className={cn(
                                        "h6 tw:m-0 tw:text-muted-foreground opacity-50"
                                    )}
                                >
                                    <s>
                                        {formatPrice(
                                            cartItem.articleStrikethroughPrice ??
                                                undefined
                                        )}
                                    </s>
                                </div>
                            )}
                            <div
                                className={cn(
                                    "tw:font-bold tw:text-xl tw:mb-0"
                                )}
                            >
                                {formatPrice(
                                    cartItem.articleSalesPrice,
                                    cartItem.free
                                        ? { freeLabel: true, t: t }
                                        : undefined
                                )}
                            </div>
                        </>
                    )}
                </div>
                <div
                    className={cn(
                        "col-8 col-sm-5 col-md-2 col-lg-2 offset-0 offset-sm-3 offset-md-0 tw:text-left tw:md:text-center"
                    )}
                >
                    <div className={cn("tw:md:flex tw:justify-center")}>
                        {!(
                            cartItem.warrantyDeposit ||
                            cartItem.articleConfiguration
                        ) && (
                            <Amount
                                onQuantityChange={(newQuantity, oldQuantity) =>
                                    onQuantityChange(newQuantity, oldQuantity)
                                }
                                value={cartItem.quantity}
                                roundingValue={cartItem.product?.roundingValue}
                                minimumOrderQuantity={minimumOrderQuantity}
                                activeDelivery={cartItem.deliveryType}
                                isFree={cartItem.free}
                                isFreeEntryAddedByCustomer={
                                    cartItem.freeEntryAddedByCustomer
                                }
                                isUpdatingCart={isUpdatingCart}
                                hasProductMultipleCartEntries={
                                    hasProductMultipleCartEntries
                                }
                            />
                        )}
                        {(cartItem.warrantyDeposit ||
                            cartItem.articleConfiguration) &&
                            cartItem.quantity}
                    </div>
                </div>
                <div
                    className={cn(
                        "col-4 col-sm-3 col-md-2 col-lg-1 offset-0 offset-sm-1 offset-md-0 tw:text-right tw:pl-0"
                    )}
                >
                    <CartItemPrice
                        totalPrice={cartItem.totalPrice}
                        free={cartItem.free}
                        recupel={cartItem.product?.recupel}
                    />
                </div>
            </Row>

            {cartItem.id && additionalServicesCartItems.length > 0 && (
                <Row>
                    <div className="col-12 col-sm-9 offset-0 offset-sm-3 col-md-10 offset-md-2">
                        <AdditionalServices
                            cartItemId={cartItem.id}
                            additionalServices={additionalServicesCartItems}
                            onAddAdditionalService={onAddAdditionalService}
                            onRemoveAdditionalService={
                                onRemoveAdditionalService
                            }
                        />
                    </div>
                </Row>
            )}

            {((cartItem.id && cartItem.product?.references?.accessories) || [])
                .length > 0 && (
                <Row>
                    <div className="col-12 col-sm-9 offset-0 offset-sm-3 col-md-10 offset-md-2">
                        <Accessories
                            accessories={
                                cartItem.product?.references?.accessories || []
                            }
                            onAdd={(sku, product) =>
                                onAddAccessory(cartItem.id!, sku, product)
                            }
                        />
                    </div>
                </Row>
            )}

            {!cartItem.warrantyDeposit && (
                <Row className={cn("tw:pt-3 tw:md:pt-0")}>
                    <div className={cn("col-12 tw:text-right")}>
                        {!cartItem.articleConfiguration && (
                            <WishlistButton
                                addToWishlist={onMoveToWishlist}
                                removeFromWishlist={onRemoveFromWishlist}
                                wishlists={wishlists}
                                sku={cartItem.sku}
                                label={() =>
                                    t("general.cart.item.moveToWishlist")
                                }
                                className="ml-3"
                                iconSize=""
                            />
                        )}
                        <SwitchDelivery
                            availableMethods={
                                cartItem.product?.deliveryTypes || []
                            }
                            isFree={cartItem.free}
                            deliveryType={cartItem.deliveryType}
                            changeDeliveryType={changeDeliveryType}
                            productVisibilityType={
                                cartItem.product?.productVisibilityType
                            }
                            sku={cartItem.sku}
                            isUpdatingCart={isUpdatingCart}
                        />
                        {productConfiguration &&
                            !productConfiguration.editUrl && (
                                <>
                                    <Button
                                        test={`cartItem-${cartItem.sku}-button-edit-product-configuration`}
                                        color="link"
                                        type="button"
                                        onClick={() => {
                                            setModalOpen(true);
                                        }}
                                        className="ml-3"
                                        disabled={isUpdatingCart}
                                    >
                                        <i className="las la-pencil-alt mr-1" />
                                        {t("general.cart.item.customize.label")}
                                    </Button>
                                    <Modal
                                        modalType={
                                            ModalType.ProductConfiguration
                                        }
                                        title={t(
                                            "general.cart.item.customize.title"
                                        )}
                                        open={modalOpen}
                                        setOpen={(toggle) => {
                                            setModalOpen(toggle);
                                            setError(false);
                                        }}
                                        confirmButtonText={t(
                                            "general.address.modal.button.label"
                                        )}
                                        onConfirm={() => {
                                            updateCartItemConfig(
                                                cartItem.id!,
                                                configuration,
                                                productConfiguration.productSubType
                                            ).then((action) => {
                                                if (
                                                    action.type ===
                                                    APIStatus.SUCCESS
                                                ) {
                                                    setModalOpen(false);
                                                    setError(false);
                                                } else if (
                                                    action.type ===
                                                    APIStatus.FAILURE
                                                ) {
                                                    setError(true);
                                                }
                                            });
                                        }}
                                        isConfirmDisabled={!configurationValid}
                                    >
                                        {error && (
                                            <div className="mt-2">
                                                <ErrorMessage
                                                    message={t(
                                                        "general.cart.item.update.failed"
                                                    )}
                                                />
                                            </div>
                                        )}
                                        <productConfiguration.visual
                                            configuration={configuration}
                                            metaConfiguration={{
                                                paintConfiguratorPath
                                            }}
                                            setConfigurationValid={
                                                setConfigurationValid
                                            }
                                            onConfigurationChange={(
                                                newConfig
                                            ) =>
                                                setConfiguration((config) => ({
                                                    ...config,
                                                    ...newConfig
                                                }))
                                            }
                                        />
                                    </Modal>
                                </>
                            )}
                        {productConfiguration?.editUrl && (
                            <Anchor
                                color="link"
                                className={cn("tw:ml-3")}
                                disabled={isUpdatingCart}
                                test={`cartItem-${cartItem.sku}-link-edit-product-configuration`}
                                href={productConfiguration.editUrl({
                                    colorId: cartItem.paintInfo?.colorId,
                                    colorHex: cartItem.paintInfo?.colorHex,
                                    colorName: cartItem.paintInfo?.colorName,
                                    paintConfiguratorPath
                                })}
                            >
                                <i className="las la-pencil-alt mr-1" />
                                {t("general.cart.item.customize.label")}
                            </Anchor>
                        )}
                        {!hideDeleteButton(cartItem) && (
                            <Button
                                test={`cartItem-${cartItem.sku}-button-delete`}
                                color="link"
                                type="button"
                                onClick={() => {
                                    removeFromCart(
                                        cartItem.id!,
                                        cartItem.quantity,
                                        cartItem.freeEntryAddedByCustomer
                                    );
                                }}
                                className="ml-3"
                                disabled={isUpdatingCart}
                            >
                                <i className="las la-trash mr-1" />
                                {t("general.cart.item.delete.label")}
                            </Button>
                        )}
                    </div>
                </Row>
            )}
        </div>
    );
};

export default CartItem;
